import { useState, useEffect, useCallback } from 'react'
import { Joyride, STATUS } from 'react-joyride'
import { useNavigate } from 'react-router-dom'
import { useWedding } from '../context/WeddingContext'
import { supabase } from '../lib/supabase'
import { fireConfetti } from '../lib/animations'

const TOUR_KEY = 'okoiya_tour_done'

const STEPS = [
  {
    target: '[data-tour="nav-events"]',
    title: 'My Events',
    content: 'Everything starts here — add your White Wedding, Traditional, Reception, or any ceremony you\'re planning.',
    placement: 'right',
    disableBeacon: true,
  },
  {
    target: '[data-tour="add-event-btn"]',
    title: 'Add your first event',
    content: 'Tap here to create an event — give it a name, a date, and a venue. It only takes a moment.',
    placement: 'bottom',
    disableBeacon: true,
    spotlightClicks: true,
  },
  {
    target: '[data-tour="nav-guests"]',
    title: 'Guests & RSVP',
    content: 'Add your full guest list and send every person a personal WhatsApp RSVP link — no app or login needed.',
    placement: 'right',
    disableBeacon: true,
  },
  {
    target: '[data-tour="nav-registry"]',
    title: 'Registry & Gifts',
    content: 'Build your wish list. Guests can browse and reserve gifts directly from your wedding portal.',
    placement: 'right',
    disableBeacon: true,
  },
  {
    target: '[data-tour="nav-asoebi"]',
    title: 'Aso Ebi',
    content: 'Manage fabric orders, collect payments, and run your Aso Ebi lookbook — all in one place.',
    placement: 'right',
    disableBeacon: true,
  },
  {
    target: '[data-tour="nav-profile"]',
    title: 'Your Love Story',
    content: 'Write your story together. Guests read it on your wedding portal while exploring your page.',
    placement: 'right',
    disableBeacon: true,
  },
  {
    target: '[data-tour="nav-planning"]',
    title: 'Planning',
    content: 'Your full wedding checklist — broken into monthly milestones from 12 months out to the final week.',
    placement: 'right',
    disableBeacon: true,
  },
]

// ── Custom Okoiya tooltip ─────────────────────────────────
function OkoiyaTooltip({ index, isLastStep, size, step, backProps, closeProps, primaryProps, skipProps, tooltipProps }) {
  return (
    <div {...tooltipProps} style={{
      background: '#FEFCFA',
      borderRadius: '1rem',
      padding: '1.375rem 1.5rem 1.25rem',
      maxWidth: 300,
      width: 'min(300px, 88vw)',
      boxShadow: '0 12px 48px rgba(0,0,0,0.14)',
      position: 'relative',
    }}>
      {/* Skip link */}
      <button {...skipProps} style={{
        position: 'absolute', top: 12, right: 14,
        background: 'none', border: 'none', cursor: 'pointer',
        fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.7rem',
        color: '#BCBCBC', padding: '2px 4px', lineHeight: 1,
      }}>
        Skip tour
      </button>

      {/* Title */}
      {step.title && (
        <p style={{
          fontFamily: 'Gibs, serif', fontWeight: 400, fontSize: '1rem',
          color: '#1A1A1A', margin: '0 0 0.375rem', paddingRight: '4rem', lineHeight: 1.3,
        }}>
          {step.title}
        </p>
      )}

      {/* Body */}
      <p style={{
        fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.82rem',
        color: '#6B6B6B', margin: '0 0 1.125rem', lineHeight: 1.65,
      }}>
        {step.content}
      </p>

      {/* Footer */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {/* Progress dots */}
        <div style={{ display: 'flex', gap: 5 }}>
          {Array.from({ length: size }).map((_, i) => (
            <div key={i} style={{
              width: 6, height: 6, borderRadius: '50%',
              background: i === index ? '#C4826A' : '#E5E0D8',
              transition: 'background 0.2s',
            }} />
          ))}
        </div>

        {/* Buttons */}
        <div style={{ display: 'flex', gap: '0.375rem', alignItems: 'center' }}>
          {index > 0 && (
            <button {...backProps} style={{
              fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.78rem',
              color: '#BCBCBC', background: 'none', border: 'none',
              cursor: 'pointer', padding: '0.4rem 0.5rem',
            }}>
              ← Back
            </button>
          )}
          <button {...(isLastStep ? closeProps : primaryProps)} style={{
            fontFamily: 'Nunito Sans, sans-serif', fontWeight: 700, fontSize: '0.8rem',
            color: 'white', background: '#C4826A', border: 'none', borderRadius: 100,
            padding: '0.5rem 1.125rem', cursor: 'pointer',
          }}>
            {isLastStep ? 'Done 🎉' : 'Next →'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Welcome modal ─────────────────────────────────────────
function WelcomeModal({ onStart, onSkip }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 900,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(3px)',
    }}>
      <style>{`
        @keyframes okoiyaWelcomeIn {
          from { opacity: 0; transform: scale(0.92); }
          to   { opacity: 1; transform: scale(1); }
        }
      `}</style>
      <div style={{
        background: '#FEFCFA', borderRadius: '1.5rem',
        padding: '2.5rem 2rem', maxWidth: 380, width: '90%',
        textAlign: 'center', boxShadow: '0 24px 64px rgba(0,0,0,0.18)',
        animation: 'okoiyaWelcomeIn 0.35s cubic-bezier(0.34,1.56,0.64,1) both',
      }}>
        <div style={{ fontSize: '1.75rem', marginBottom: '0.625rem', color: '#C4826A' }}>✦</div>
        <h2 style={{
          fontFamily: 'Gibs, serif', fontWeight: 400, fontSize: '1.45rem',
          color: '#1A1A1A', margin: '0 0 0.625rem',
        }}>
          Welcome to Okoiya
        </h2>
        <p style={{
          fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.875rem',
          color: '#6B6B6B', margin: '0 0 2rem', lineHeight: 1.7,
        }}>
          Let's set up your wedding together — a quick look at everything available to you. Takes about 2 minutes.
        </p>
        <button onClick={onStart} style={{
          width: '100%', fontFamily: 'Nunito Sans, sans-serif', fontWeight: 700,
          fontSize: '0.9rem', color: 'white', background: '#C4826A',
          border: 'none', borderRadius: 100, padding: '0.875rem',
          cursor: 'pointer', marginBottom: '0.625rem',
          transition: 'opacity 0.15s',
        }}
          onMouseEnter={e => e.currentTarget.style.opacity = '0.88'}
          onMouseLeave={e => e.currentTarget.style.opacity = '1'}
        >
          Let's go →
        </button>
        <button onClick={onSkip} style={{
          width: '100%', fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.82rem',
          color: '#BCBCBC', background: 'none', border: 'none',
          cursor: 'pointer', padding: '0.4rem',
        }}>
          Skip for now
        </button>
      </div>
    </div>
  )
}

// ── Toast ─────────────────────────────────────────────────
function Toast({ message, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 3500)
    return () => clearTimeout(t)
  }, [onClose])

  return (
    <div style={{
      position: 'fixed', bottom: 90, left: '50%',
      transform: 'translateX(-50%)',
      background: '#1A1A1A', color: 'white',
      fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.875rem', fontWeight: 600,
      borderRadius: 100, padding: '0.75rem 1.5rem',
      zIndex: 9999, boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
      whiteSpace: 'nowrap', animation: 'okoiyaToastIn 0.3s ease-out both',
    }}>
      <style>{`
        @keyframes okoiyaToastIn {
          from { opacity: 0; transform: translateX(-50%) translateY(10px); }
          to   { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
      `}</style>
      {message}
    </div>
  )
}

// ── Main ─────────────────────────────────────────────────
export default function OnboardingTour({ onOpenSidebar, onTourComplete }) {
  const { wedding } = useWedding()
  const navigate    = useNavigate()

  const [phase,     setPhase]     = useState('idle') // idle | welcome | touring | done
  const [run,       setRun]       = useState(false)
  const [showToast, setShowToast] = useState(false)

  useEffect(() => {
    if (!wedding) return
    if (window.innerWidth < 768) return // sidebar hidden on mobile — skip tour
    const done = localStorage.getItem(TOUR_KEY) === '1' || wedding.onboarding_completed === true
    if (!done) {
      const t = setTimeout(() => setPhase('welcome'), 900)
      return () => clearTimeout(t)
    }
  }, [wedding?.id])

  function markDone() {
    localStorage.setItem(TOUR_KEY, '1')
    onTourComplete?.()
    if (wedding?.id) {
      supabase.from('weddings')
        .update({ onboarding_completed: true })
        .eq('id', wedding.id)
        .then(() => {})
    }
  }

  function handleStart() {
    onOpenSidebar?.()
    navigate('/dashboard/events')
    setPhase('touring')
    // Small delay so /events is rendered before joyride scans for targets
    setTimeout(() => setRun(true), 160)
  }

  function handleSkipWelcome() {
    markDone()
    setPhase('done')
  }

  const handleJoyride = useCallback(({ status }) => {
    if (status === STATUS.FINISHED) {
      setRun(false)
      markDone()
      fireConfetti()
      setShowToast(true)
      setPhase('done')
    } else if (status === STATUS.SKIPPED) {
      setRun(false)
      markDone()
      setPhase('done')
    }
  }, [wedding?.id])

  const toast = showToast && (
    <Toast
      message="You're all set! Time to start planning 🎉"
      onClose={() => setShowToast(false)}
    />
  )

  if (phase === 'idle' || phase === 'done') return toast || null

  return (
    <>
      {phase === 'welcome' && (
        <WelcomeModal onStart={handleStart} onSkip={handleSkipWelcome} />
      )}

      {phase === 'touring' && (
        <Joyride
          steps={STEPS}
          run={run}
          continuous
          scrollToFirstStep={false}
          disableScrolling
          disableOverlayClose
          disableCloseOnEsc
          showSkipButton
          tooltipComponent={OkoiyaTooltip}
          callback={handleJoyride}
          floaterProps={{ disableAnimation: true }}
          styles={{
            options: {
              overlayColor: 'rgba(0,0,0,0.42)',
              zIndex: 500,
            },
            spotlight: {
              borderRadius: '0.75rem',
            },
          }}
        />
      )}

      {toast}
    </>
  )
}
